// TASK-N6 — single source of truth for the visit-report 2-query data flow
// (SR + patient). Coordinator subtree resolves useFetcher() → apiFetch;
// carer subtree → carerFetch (silent refresh on 401). isAtWanderingRisk is
// derived from the patient's chronic_conditions; mirrors the predicate
// previously inlined in VisitReportPage.tsx pre-N1a lift.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { MobilityStatus, VisitType } from "@hcm/core";
import { useFetcher } from "./useFetcher.js";

export interface SrSummary {
  _id: string;
  patient_id: string;
  nsofar_sr_id?: string | null;
  scheduled_datetime?: string | null;
  visit_type?: VisitType | null;
}

export interface PatientSummary {
  _id: string;
  full_name?: string | null;
  chronic_conditions?: string[];
  baseline_mobility?: MobilityStatus | null;
}

export interface VisitData {
  ready: boolean;
  srData?: SrSummary;
  patientData?: PatientSummary;
  isAtWanderingRisk: boolean;
  isError: boolean;
  error: unknown;
}

export function useVisitReportData(id: string): VisitData {
  const fetcher = useFetcher();
  const { data: srData, isLoading: srLoading, isError: srIsError, error: srError } = useQuery<SrSummary>({
    queryKey: ["sr", id],
    queryFn: () => fetcher<SrSummary>(`/sr/${id}`),
    enabled: !!id,
  });
  const patientId = srData?.patient_id;
  const { data: patientData, isLoading: patientLoading, isError: patientIsError, error: patientError } = useQuery<PatientSummary>({
    queryKey: ["patient", patientId],
    queryFn: () => fetcher<PatientSummary>(`/patients/${patientId ?? ""}`),
    enabled: !!patientId,
  });
  const isAtWanderingRisk = useMemo(() => {
    const conditions = patientData?.chronic_conditions;
    if (!conditions?.length) return false;
    return conditions.some((c) => c === "dementia_alzheimers" || c === "parkinson_disease");
  }, [patientData?.chronic_conditions]);
  const ready = !srLoading && !patientLoading && !!srData && !!patientData;
  const isError = srIsError || patientIsError;
  const error = srError ?? patientError;
  return { ready, srData, patientData, isAtWanderingRisk, isError, error };
}
